import { useEffect, useRef } from "react";

const DEFAULT_COARSE_STEP_SEC = 3;
const FRAME_SEC = 1 / 30;
// Strike-zone nudge steps as a fraction of the video-box (the box geometry is
// fractional so it scales with the video). Small = fine adjustment.
const MOVE_STEP = 0.004;
const RESIZE_STEP = 0.005;

// Don't hijack keys while the user is typing in a field. This (plus
// preventDefault below) is the fix for the "±3s sometimes runs away" bug:
// the legacy handler fired on every keystroke (`if (true || e.ctrlKey)`) and
// never called preventDefault, so arrows ALSO triggered the native <video>
// ±5s seek and page scroll.
const isEditableTarget = (el) =>
  !!el &&
  (el.tagName === "INPUT" ||
    el.tagName === "TEXTAREA" ||
    el.tagName === "SELECT" ||
    el.isContentEditable);

/**
 * Centralised keyboard control for the recording page. Key positions match the
 * legacy scheme exactly; only the behaviour is fixed (preventDefault, typing
 * guard) and the coarse seek step is configurable.
 */
export const useHotkeys = ({
  playerRef,
  coarseStepSec = DEFAULT_COARSE_STEP_SEC,
  onSeekBy,
  onStepFrame,
  onMoveBox,
  onResizeBox,
  onTogglePlay,
  onMarkStart,
  onMarkEnd,
  onToggleLock,
  onPickoff,
  enabled = true,
}) => {
  // Latest props in a ref so the document listener (bound once) never reads a
  // stale closure — the legacy `useEffect([])` captured stale state.
  const stateRef = useRef();
  stateRef.current = {
    playerRef,
    coarseStepSec,
    onSeekBy,
    onStepFrame,
    onMoveBox,
    onResizeBox,
    onTogglePlay,
    onMarkStart,
    onMarkEnd,
    onToggleLock,
    onPickoff,
    enabled,
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      const s = stateRef.current;
      if (!s.enabled || e.defaultPrevented || isEditableTarget(e.target)) return;
      if (e.target?.closest?.('[role="dialog"], [role="menu"], [role="slider"]')) return;
      if ((e.code === "Space" || e.code === "Enter") && e.target?.closest?.('button, a, [role="button"]')) return;
      // Let browser/OS chords through (e.g. Cmd+ArrowRight, Ctrl+Space) — our
      // shortcuts are all unmodified single keys. Shift is allowed (e.code is
      // shift-independent, so Shift+D still resolves to KeyD).
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const seek = (delta) => {
        if (s.onSeekBy) { s.onSeekBy(delta); return; }
        const player = s.playerRef.current;
        if (player) player.seekTo(player.getCurrentTime() + delta, "seconds");
      };

      // Key off e.code (physical position) — NOT e.key — so the controls stay
      // bound to the same physical keys regardless of keyboard layout or IME,
      // matching the legacy keyCode behaviour (recorders may have a CJK IME on).
      let handled = true;
      switch (e.code) {
        case "KeyD": s.onMoveBox?.(MOVE_STEP, 0); break;
        case "KeyA": s.onMoveBox?.(-MOVE_STEP, 0); break;
        case "KeyW": s.onMoveBox?.(0, -MOVE_STEP); break;
        case "KeyS": s.onMoveBox?.(0, MOVE_STEP); break;
        case "KeyE": s.onResizeBox?.(-RESIZE_STEP); break;
        case "KeyR": s.onResizeBox?.(RESIZE_STEP); break;
        case "Comma": s.onStepFrame ? s.onStepFrame(-1) : seek(-FRAME_SEC); break;
        case "Period": s.onStepFrame ? s.onStepFrame(1) : seek(FRAME_SEC); break;
        case "Space": s.onTogglePlay?.(); break;
        case "ArrowLeft": seek(-s.coarseStepSec); break;
        case "ArrowRight": seek(s.coarseStepSec); break;
        case "KeyI": s.onMarkStart?.(); break;
        case "KeyO": s.onMarkEnd?.(); break;
        case "KeyL": s.onToggleLock?.(); break;
        // Pickoff is a sudden event recorded with both hands on the keyboard —
        // ignore key-repeat (holding P down) so it can't fire the socket emit
        // over and over.
        case "KeyP": if (!e.repeat) s.onPickoff?.(); break;
        default: handled = false;
      }
      if (handled) e.preventDefault();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);
};
